import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { IMessage, IMessageChannel, IMessageQuery } from '@curbnturf/entities';
import { Observable } from 'rxjs';
import { BASE_API_URL } from '../network/api-config';

const API_URL = BASE_API_URL + 'message';

/**
 * Local fork of @curbnturf/message's MessageService — trimmed to plain
 * HttpClient. Full method set (all 4 are used by MessageService's app-facing
 * wrapper in ../messaging/message.service.ts).
 */
@Injectable({ providedIn: 'root' })
export class MessageApiService {
  private readonly http = inject(HttpClient);

  create(message: IMessage): Observable<IMessageChannel> {
    return this.http.post<IMessageChannel>(API_URL, this.formatForServer(message));
  }

  fetch(propertyId: number): Observable<IMessageChannel> {
    if (!propertyId) {
      throw new Error('Unable to fetch property messages. No propertyId provided');
    }

    return this.http.get<IMessageChannel>(`${API_URL}/${propertyId}`);
  }

  query(query: IMessageQuery): Observable<IMessageChannel[]> {
    const queryString = this.queryToString(query);
    if (!queryString) {
      throw new Error('Unable to query for messages. No query parameters provided');
    }

    return this.http.get<IMessageChannel[]>(`${API_URL}?${queryString}`);
  }

  read(channel: IMessageChannel): Observable<IMessageChannel> {
    if (!channel || !channel.id) {
      throw new Error('Unable to mark message as read. Invalid message channel provided');
    }

    return this.http.post<IMessageChannel>(`${API_URL}/${channel.id}/read`, null);
  }

  private formatForServer(message: IMessage): IMessage {
    return { ...message };
  }

  private queryToString(query: IMessageQuery): string {
    const queryArr = [];

    for (const prop in query) {
      if (Object.prototype.hasOwnProperty.call(query, prop)) {
        queryArr.push(`${prop}=${query[prop]}`);
      }
    }

    return queryArr.join('&');
  }
}
